import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Lightbulb, Sparkles, Target, Clock, Zap, TrendingUp, Shield, Flame,
  Trophy, Loader2, CheckCircle2, XCircle, BrainCircuit, AlertCircle,
  Rocket, Star, Award, Quote,
} from 'lucide-react';
import { toast } from 'sonner';
import { actionPlanAPI } from '../lib/api';

// Map of icon keys returned by the backend -> lucide icons.
const ICONS = {
  target: Target, clock: Clock, zap: Zap, trending_up: TrendingUp,
  shield: Shield, flame: Flame, trophy: Trophy, sparkles: Sparkles,
};
const ACTION_LINKS = {
  'leads:in_progress':   { href: '/leads?status=In%20Progress', label: 'Open In-Progress' },
  'leads:pending_stale': { href: '/leads?status=Pending',       label: 'Open Pending'      },
  'leads:cancelled':     { href: '/leads?status=Cancelled',     label: 'Review Cancelled'  },
};

// Pool of motivational quotes — rotated by user id so each counselor gets a
// consistent (but varied between people) message.
const QUOTES = [
  "The expert in anything was once a beginner. Keep going. 🚀",
  "Small daily improvements lead to stunning results. 🌟",
  "Discipline is choosing what you want most over what you want now. 💪",
  "Success is the sum of small efforts repeated day in and day out. 🌱",
  "Champions keep playing until they get it right. 🏆",
  "Your only limit is your mind. Push through it. 🧠",
  "Excellence is not a skill, it's an attitude. ✨",
  "Believe you can, and you're halfway there. ⭐",
];

function pickQuote(seed) {
  let h = 0;
  for (const ch of (seed || 'anon')) h = (h * 31 + ch.charCodeAt(0)) | 0;
  return QUOTES[Math.abs(h) % QUOTES.length];
}

// Custom markdown renderers — give the AI plan a richer, branded look.
const planMdComponents = {
  h3: ({ children, ...props }) => {
    const text = String(children?.[0] || children || '').trim();
    let bg = 'from-purple-50 to-indigo-50 border-purple-100 text-purple-900';
    let Icon = Sparkles;
    if (/quick wins?/i.test(text))   { bg = 'from-amber-50 to-yellow-50 border-amber-200 text-amber-900';   Icon = Zap; }
    if (/4[-\s]?week/i.test(text))   { bg = 'from-blue-50 to-sky-50 border-blue-200 text-blue-900';         Icon = Rocket; }
    if (/skill/i.test(text))         { bg = 'from-emerald-50 to-green-50 border-emerald-200 text-emerald-900'; Icon = TrendingUp; }
    if (/stretch/i.test(text))       { bg = 'from-pink-50 to-rose-50 border-pink-200 text-pink-900';        Icon = Star; }
    return (
      <h3 className={`bg-gradient-to-r ${bg} border rounded-md px-3 py-2 mt-4 mb-2 flex items-center gap-2 not-prose font-semibold`} {...props}>
        <Icon className="h-4 w-4 shrink-0" /> {children}
      </h3>
    );
  },
  li: ({ children, ...props }) => (
    <li className="my-1 marker:text-purple-500" {...props}>{children}</li>
  ),
  strong: ({ children, ...props }) => (
    <strong className="text-slate-900 font-semibold" {...props}>{children}</strong>
  ),
};

export function MyActionPlanCard({ testIdSuffix = '' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const refetch = async () => {
    setLoading(true);
    try { const r = await actionPlanAPI.mine(); setData(r.data); }
    catch (_) { /* silent */ }
    setLoading(false);
  };
  useEffect(() => { refetch(); }, []);

  const onRequestAI = async () => {
    setSubmitting(true);
    try {
      const r = await actionPlanAPI.requestAI();
      if (r.data?.already_pending) toast.info('Your earlier request is still awaiting admin approval');
      else toast.success('Coaching request sent — an admin will approve it shortly');
      await refetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send request');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm" data-testid={`my-action-plan-card${testIdSuffix}`}>
        <CardContent className="py-6 text-sm text-slate-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your action plan…
        </CardContent>
      </Card>
    );
  }
  if (!data || !data.tips) return null;

  const sn = data.snapshot || {};
  const latest = data.latest_ai_request;
  const isPending  = latest && latest.status === 'pending';
  const isApproved = latest && (latest.status === 'approved' || latest.status === 'approved_no_plan') && latest.ai_plan;
  const isRejected = latest && latest.status === 'rejected';
  const isStale    = !!data.ai_plan_is_stale;
  const quote      = pickQuote(sn.counselor_id || 'me');

  return (
    <Card className="border-0 shadow-sm overflow-hidden" data-testid={`my-action-plan-card${testIdSuffix}`}>
      {/* Motivational hero strip */}
      <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-pink-50 px-5 py-3 border-b border-amber-100 flex items-start gap-3">
        <Quote className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
        <p className="text-sm font-medium italic text-slate-700">{quote}</p>
      </div>

      <CardHeader className="pb-3 pt-4">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <Lightbulb className="h-5 w-5 text-amber-500" /> My Action Plan
          {sn.my_rank && (
            <Badge className="bg-emerald-50 border-emerald-200 text-emerald-700">
              <Award className="h-3 w-3 mr-1" /> Rank #{sn.my_rank} of {sn.total_in_cohort}
            </Badge>
          )}
          <Badge variant="outline" className="ml-auto bg-amber-50 border-amber-200 text-amber-700">
            Personalised tips
          </Badge>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Rule-based tips */}
        <div className="space-y-2" data-testid="rule-tips-list">
          {data.tips.map((tip, i) => {
            const Icon = ICONS[tip.icon] || Sparkles;
            const link = tip.action ? ACTION_LINKS[tip.action] : null;
            return (
              <div key={i}
                   className="flex items-start gap-3 rounded-lg border border-slate-200 bg-gradient-to-r from-slate-50 to-white px-3 py-2 hover:shadow-sm transition-shadow"
                   data-testid={`tip-${i}`}>
                <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white shrink-0">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{tip.title}</p>
                  <p className="text-xs text-slate-600 mt-0.5">{tip.detail}</p>
                </div>
                {link && (
                  <a href={link.href}
                     className="text-xs font-medium text-blue-600 hover:underline whitespace-nowrap mt-1">
                    {link.label} →
                  </a>
                )}
              </div>
            );
          })}
        </div>

        {/* AI coaching block */}
        <div className="pt-3 border-t border-slate-100" data-testid="ai-coaching-block">
          {!latest && (
            <div className="flex items-center justify-between gap-3 flex-wrap rounded-md bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 px-3 py-2">
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <BrainCircuit className="h-4 w-4 text-purple-600" />
                <span>Want a personalised 4-week AI coaching plan?</span>
              </div>
              <Button size="sm" onClick={onRequestAI} disabled={submitting}
                      data-testid="request-ai-coaching-btn"
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <BrainCircuit className="h-4 w-4 mr-1" />}
                Request AI Coaching
              </Button>
            </div>
          )}
          {isPending && (
            <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 rounded-md px-3 py-2 border border-amber-200"
                 data-testid="ai-pending-banner">
              <Clock className="h-4 w-4 animate-pulse" />
              Your AI coaching request is awaiting admin approval.
            </div>
          )}
          {isRejected && (
            <div className="flex items-center justify-between gap-3 flex-wrap rounded-md bg-rose-50 border border-rose-200 px-3 py-2">
              <div className="flex items-center gap-2 text-sm text-rose-700">
                <XCircle className="h-4 w-4" /> Your last request was declined.
              </div>
              <Button size="sm" onClick={onRequestAI} disabled={submitting}
                      data-testid="request-ai-again-btn"
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white">
                Request Again
              </Button>
            </div>
          )}
          {isApproved && (
            <div className="rounded-xl border border-purple-200 overflow-hidden shadow-sm" data-testid="ai-coaching-plan">
              {/* Plan header */}
              <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 px-4 py-3 text-white flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  <div>
                    <p className="font-semibold leading-tight">Your AI Coaching Plan</p>
                    <p className="text-xs text-purple-100">Generated by {latest.ai_model || 'AI'}</p>
                  </div>
                </div>
                {isStale && (
                  <div className="flex items-center gap-2">
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                      <AlertCircle className="h-3 w-3 mr-1" /> Plan is over {data.stale_threshold_days || 21} days old
                    </Badge>
                    <Button size="sm" onClick={onRequestAI} disabled={submitting}
                            className="bg-white text-purple-700 hover:bg-purple-50"
                            data-testid="request-ai-refresh-btn">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <BrainCircuit className="h-4 w-4 mr-1" />}
                      Request Refresh
                    </Button>
                  </div>
                )}
              </div>
              {/* Markdown body */}
              <div className="p-5 bg-white prose prose-sm max-w-none text-slate-700
                              prose-headings:text-slate-900
                              prose-h3:font-bold prose-h3:text-base prose-h3:mt-3
                              prose-strong:text-slate-900
                              prose-ul:my-2 prose-li:my-1">
                <ReactMarkdown components={planMdComponents}>
                  {latest.ai_plan}
                </ReactMarkdown>
              </div>
              <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between">
                <span>You can request a fresh plan anytime — admin approval is required.</span>
                {!isStale && (
                  <Button size="sm" variant="ghost" onClick={onRequestAI} disabled={submitting}
                          data-testid="request-ai-refresh-btn">
                    <BrainCircuit className="h-3.5 w-3.5 mr-1" /> Request New Plan
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default MyActionPlanCard;
